import { Checkbox } from '@components/common/Checkbox/Checkbox';
import { Divider } from '@components/common/Divider/Divider';
import { TooltipProps } from '@components/common/Tooltip/Tooltip';
import { MenuItem as HeadlessMenuItem } from '@headlessui/react';
import { BothOrNeither } from '@utils';
import clsx from 'clsx';
import { ReactNode } from 'react';

type BaseMenuItemType = {
  id: string;
  content: ReactNode;
  onClick: () => void;
  checked?: boolean;
  isDivider?: false;
};

type DividerMenuItemType = {
  id: string;
  isDivider: true;
};

type DisabledProps = BothOrNeither<
  { disabled: boolean },
  { disabledTooltip: TooltipProps }
>;

export type MenuItemType =
  | (BaseMenuItemType & DisabledProps)
  | DividerMenuItemType;

type Props = {
  item: MenuItemType;
  handleItemClick: (item: MenuItemType, close: () => void) => void;
  disableAutoClose?: boolean;
};

export const MenuItem = ({
  item,
  handleItemClick,
  disableAutoClose,
}: Props) => {
  if (item.isDivider) {
    return <Divider key={item.id} />;
  }
  return (
    <HeadlessMenuItem key={item.id}>
      {({ focus, close }) => (
        <div
          role="menuitem"
          tabIndex={0}
          className={clsx(
            focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
            'group flex w-full items-center px-4 py-1.5 text-sm',
          )}
          onClick={(e) => {
            if (disableAutoClose) {
              e.preventDefault();
              e.stopPropagation();
            }
            handleItemClick(item, close);
          }}
        >
          {'checked' in item ? (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                name={item.id}
                label={item.content}
                checked={item.checked as boolean}
                onChange={() => handleItemClick(item, close)}
                disabled={item.disabled as boolean}
                disabledTooltip={item.disabledTooltip as TooltipProps}
              />
            </div>
          ) : (
            <button
              className="flex w-full items-center"
              onClick={() => handleItemClick(item, close)}
              disabled={item.disabled as boolean}
            >
              {item.content}
            </button>
          )}
        </div>
      )}
    </HeadlessMenuItem>
  );
};
